import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface PortalPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
  isWideOnMobile?: boolean;
}

interface Coords {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  maxHeight?: number;
  isMobileFull?: boolean;
}

export const PortalPopover: React.FC<PortalPopoverProps> = ({
  isOpen,
  onClose,
  triggerRef,
  children,
  align = 'left',
  className = '',
  isWideOnMobile = false,
}) => {
  const [coords, setCoords] = useState<Coords | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    const margin = 4;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 640;

    // If trigger element has zero size (e.g. during re-render or layout pass), skip calculation
    if (rect.width === 0 && rect.height === 0) {
      return;
    }

    // If trigger element has been scrolled completely offscreen, dismiss popover
    if (
      rect.bottom < -20 ||
      rect.top > viewportHeight + 20 ||
      rect.right < -20 ||
      rect.left > viewportWidth + 20
    ) {
      onClose();
      return;
    }

    // Vertical placement: decide whether to open below or flip above
    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openAbove = spaceBelow < 250 && spaceAbove > spaceBelow;

    let vPos: { top?: number; bottom?: number; maxHeight: number };
    if (openAbove) {
      const bottom = Math.max(8, viewportHeight - rect.top + margin);
      const maxHeight = Math.max(160, Math.min(spaceAbove - 16, 520));
      vPos = { bottom, maxHeight };
    } else {
      const top = Math.max(8, rect.bottom + margin);
      const maxHeight = Math.max(160, Math.min(spaceBelow - 16, 520));
      vPos = { top, maxHeight };
    }

    if (isMobile && isWideOnMobile) {
      // On mobile screens, wide popovers (Filter / Sort) fit full screen width with 8px margins
      setCoords({
        ...vPos,
        left: 8,
        right: 8,
        isMobileFull: true,
      });
      return;
    }

    // Horizontal placement with viewport boundary clamping
    const popoverWidth = popoverRef.current?.offsetWidth || 256;
    const padding = 8;

    if (align === 'right') {
      const right = Math.max(padding, Math.min(viewportWidth - rect.right, viewportWidth - popoverWidth - padding));
      setCoords({
        ...vPos,
        right,
      });
    } else {
      // If left-alignment would overflow the right edge of viewport, align to right or clamp
      if (rect.left + popoverWidth > viewportWidth - padding) {
        const right = Math.max(padding, Math.min(viewportWidth - rect.right, viewportWidth - popoverWidth - padding));
        setCoords({
          ...vPos,
          right,
        });
      } else {
        const left = Math.max(padding, Math.min(rect.left, viewportWidth - popoverWidth - padding));
        setCoords({
          ...vPos,
          left,
        });
      }
    }
  }, [triggerRef, align, isWideOnMobile, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    // Re-check after DOM mount to refine position with exact measured popover dimensions
    const raf = requestAnimationFrame(() => {
      updatePosition();
    });

    const handleScrollOrResize = () => {
      updatePosition();
    };

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, updatePosition, onClose]);

  if (!isOpen || !coords || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        ...(coords.top !== undefined ? { top: coords.top } : {}),
        ...(coords.bottom !== undefined ? { bottom: coords.bottom } : {}),
        ...(coords.left !== undefined ? { left: coords.left } : {}),
        ...(coords.right !== undefined ? { right: coords.right } : {}),
        maxHeight: coords.maxHeight ? `${coords.maxHeight}px` : 'calc(100vh - 32px)',
        maxWidth: 'calc(100vw - 16px)',
      }}
      className={`z-[70] overflow-y-auto ${className}`}
    >
      {children}
    </div>,
    document.body
  );
};
